import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Pagination } from '@lsq-frontend/angular-shared/api-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import {
    atlas,
    SETTINGS_SCF_DIRECT_BANK_ACCOUNTS
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    GetKillSwitchActiveRequest,
    GetKillSwitchActiveService,
    ListBankAccountForSellerService,
    ListRedactedBankAccountForSellerRequest,
    RedactedBankAccount
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import { AnnouncementType, forLifeOf, LifecycleAware } from '@lsq-frontend/common-ui';
import {
    AnnouncementService,
    FeatureFlagService,
    getRouterLinkUrlById,
    Icons
} from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { combineLatest, ReplaySubject } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';
import { EnrollmentService } from '../../../../enrollment/enrollment.service';
import { TAKE_ALL } from '../../../constants';

@LifecycleAware()
@Injectable({ providedIn: 'root' })
export class ScfSellerService {
    private _noVerifiedBannerMessage = this._i18n(
        'Your default payment method is currently under review. Early payments cannot be requested or automatically processed until it is verified. You’ll get an email once this process is complete.'
    );
    private _killswitchBannerMessage = this._i18n(
        'Early Payment is temporarily unavailable. Please check back soon.'
    );

    public bankAccounts$ = new ReplaySubject<RedactedBankAccount[]>(1);
    isKillSwitchEngaged$ = new ReplaySubject<boolean>(1);

    public hasVerifiedBankAccount$ = this.bankAccounts$.pipe(
        map((bankAccounts) => bankAccounts.some((bankAccount) => bankAccount.isVerified)),
        startWith(false),
        shareReplay(1)
    );

    public hasVerifiedBankAccountStartTrue$ = this.bankAccounts$.pipe(
        map((bankAccounts) => bankAccounts.some((bankAccount) => bankAccount.isVerified)),
        startWith(true),
        shareReplay(1)
    );

    constructor(
        private _userService: UserService,
        private _getKillSwitchActiveService: GetKillSwitchActiveService,
        private _listBankAccountForSellerService: ListBankAccountForSellerService,
        private _featureFlagService: FeatureFlagService,
        private _announcementService: AnnouncementService,
        private _i18n: I18n,
        private router: Router,
        private _enrollmentService: EnrollmentService
    ) {
        this._userService.user$.subscribe(async (user) => {
            if (user?.claims?.dashboard?.includes(DashboardClaim.FinancingVendorPortal)) {
                const output = await this._listBankAccountForSellerService
                    .executeOnce(
                        new ListRedactedBankAccountForSellerRequest({
                            pagination: new Pagination({
                                take: TAKE_ALL
                            })
                        })
                    )
                    .then(({ redactedBankAccount }) => redactedBankAccount);

                this.bankAccounts$.next(output);

                const killSwitchOnResponse = await this._getKillSwitchActiveService.executeOnce(
                    new GetKillSwitchActiveRequest()
                );
                this.isKillSwitchEngaged$.next(killSwitchOnResponse.active);
            }
        });
    }

    public init(): void {
        combineLatest([
            this._userService.user$,
            this.isKillSwitchEngaged$,
            this.hasVerifiedBankAccountStartTrue$,
            this.bankAccounts$
        ])
            .pipe(forLifeOf(this))
            .subscribe(
                async ([user, isKillSwitchEngaged, hasVerifiedBankAccount, bankAccounts]) => {
                    if (user?.claims?.dashboard?.includes(DashboardClaim.FinancingVendorPortal)) {
                        if (!!bankAccounts.length && !hasVerifiedBankAccount) {
                            const enrollmentIsIncomplete = await this._enrollmentService.enrollmentIsIncomplete();

                            if (!enrollmentIsIncomplete) {
                                this._announcementService.addAnnouncement({
                                    id: AnnouncementType.SellerNoVerifiedPaymentMethod,
                                    text: this._noVerifiedBannerMessage,
                                    onClick: user.claims.dashboard.includes(
                                        DashboardClaim.FinancingViewBankAccounts
                                    )
                                        ? (): void => {
                                              this.router.navigateByUrl(
                                                  getRouterLinkUrlById(
                                                      atlas,
                                                      SETTINGS_SCF_DIRECT_BANK_ACCOUNTS
                                                  )
                                              );
                                          }
                                        : undefined,
                                    dismissable: false,
                                    icon: Icons.FaUsdCircle
                                });
                            }
                        } else {
                            this._announcementService.removeAnnouncement(
                                AnnouncementType.SellerNoVerifiedPaymentMethod
                            );
                        }
                        if (isKillSwitchEngaged && hasVerifiedBankAccount) {
                            this._announcementService.addAnnouncement({
                                id: AnnouncementType.SellerKillSwitchIsOn,
                                text: this._killswitchBannerMessage,
                                dismissable: false,
                                icon: Icons.FaExclamationTriangle
                            });
                        } else {
                            this._announcementService.removeAnnouncement(
                                AnnouncementType.SellerKillSwitchIsOn
                            );
                        }
                    } else {
                        this.removeScfSellerAnnouncements();
                    }
                }
            );
    }

    private removeScfSellerAnnouncements(): void {
        this._announcementService.removeAnnouncement(
            AnnouncementType.SellerNoVerifiedPaymentMethod
        );
        this._announcementService.removeAnnouncement(AnnouncementType.SellerKillSwitchIsOn);
    }
}
